// Who a level matchup goes to, and why a starter scored nothing. Pure.
// Scenario N1, MATCHUP.md "A result, explained".
//
// IN THE DOMAIN because the engine that settles a week and every client that
// explains it need the same answer: a screen that worked out its own winner of
// a tie would one day name a different one from the standings.
//
// THE RULE IS NAMED AND THE SIDE IS WRITTEN DOWN. Until 2026-10-02 a broken tie
// stored only the rule (`decided_by`), and every reader worked the side out
// again: `home-team` went home and anything else went to the visitors, so a
// league breaking ties on bench points gave every tie to the away team whatever
// the benches scored. `breakTie` says the side, and the scorer stores it as
// `matchups.winner_team_id` (0027).

import type { TiebreakerSettings } from '../rules/schemas';
import { matchupTiebreak } from '../schedule/bracket';
import type { GameStatus, MatchupRowView } from '../api/views';

/** How a level final was settled: the rule that broke it, and the side it went to. */
export interface TieOutcome {
  /** `higher-seed`, `home-team`, `bench-points` or `coin-flip`; null when it stays a tie. */
  decidedBy: string | null;
  winner: 'home' | 'away' | null;
}

/**
 * A level final, settled by its kind and the league's rule (`matchupTiebreak`).
 *
 * BENCH POINTS ARE THE BENCH'S, as ESPN, Yahoo and Sleeper count them: every
 * player on the bench, and nobody on IR. Benches that are level too leave the
 * game a tie, rather than reaching for a second rule nobody chose.
 *
 * A COIN FLIP IS THE MATCHUP'S OWN, not a random number: the same matchup
 * lands the same way every time it is settled, so a stat correction that
 * re-settles the week cannot flip the coin again (`coinFlip`).
 */
export function breakTie(input: {
  kind: string;
  leagueRule: TiebreakerSettings['matchup'];
  matchupId: string;
  /** Each side's bench total. Read only for `bench-points`. */
  homeBench: number;
  awayBench: number;
}): TieOutcome {
  const rule = matchupTiebreak(input.kind, input.leagueRule);
  switch (rule) {
    case null:
      return { decidedBy: null, winner: null };
    case 'higher-seed':
    case 'home-team':
      // The higher seed is the home side: `planBracket` writes it there.
      return { decidedBy: rule, winner: 'home' };
    case 'bench-points':
      if (input.homeBench === input.awayBench) return { decidedBy: null, winner: null };
      return { decidedBy: rule, winner: input.homeBench > input.awayBench ? 'home' : 'away' };
    case 'coin-flip':
      return { decidedBy: rule, winner: coinFlip(input.matchupId) };
    default:
      // A rule this function does not know is not a winner it can name.
      throw new Error(`No way to break a tie by '${rule}'.`);
  }
}

/**
 * How a tie was settled, in a verdict's few words — "won on bench points",
 * "lost on a coin flip" — the same everywhere a result is told (the matchup,
 * the recap, a notification). "on the tiebreaker" for a rule with no words.
 */
export function settledOn(decidedBy: string | null | undefined): string {
  switch (decidedBy) {
    case 'bench-points':
      return 'on bench points';
    case 'coin-flip':
      return 'on a coin flip';
    case 'home-team':
      return 'on home field';
    case 'higher-seed':
      return 'on seeding';
    default:
      return 'on the tiebreaker';
  }
}

/**
 * Heads or tails for one matchup, from its id: FNV-1a over the id, mixed
 * through MurmurHash3's finalizer, and the top bit. Never a different answer
 * for the same id. NOT FNV's low bit, which multiplying by an odd prime never
 * changes: it was only the parity of how many characters had odd codes, so
 * one home team's flips alternated week by week (the review of N1).
 */
export function coinFlip(matchupId: string): 'home' | 'away' {
  let hash = 0x811c9dc5;
  for (let i = 0; i < matchupId.length; i += 1) {
    hash ^= matchupId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return hash >>> 31 === 0 ? 'home' : 'away';
}

/**
 * Why a starter scores nothing, when the reason is plain (B-233): nobody in
 * the slot, his team without a game this week, or a game over with no line of
 * his once its lines have landed — `rowPoints` prices that as 0. Null for a
 * starter who can still score, or did; and for one whose game is over and
 * whose lines are late, which is a wait (B-172), not a zero.
 *
 * POINTS WIN. A starter with points scored them, whatever the schedule says
 * about his game: the seeded world has lines for weeks whose games are not in
 * the calendar, and a row reading "bye" beside 15.4 is a contradiction.
 */
export function noScoreOf(row: {
  player: unknown;
  game: Pick<GameStatus, 'state'> | null;
  points: number | null;
  statLine: string | null;
}): MatchupRowView['noScore'] {
  if (row.player === null) return 'empty';
  if (row.points !== null && row.points !== 0) return null;
  if (row.game?.state === 'bye') return 'bye';
  if (row.game?.state === 'final' && row.points === 0 && row.statLine === null) {
    return 'did-not-play';
  }
  return null;
}
